import React from 'react';
import { useHistory } from 'react-router-dom';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Colors, PrimaryButton, SizeableRoundedDialog, Text } from 'library';
import UserAttributesApi from 'shared/UserAttributesApi';
import { useUserContext } from 'UserContext';

import type { SubPoolSummary } from '../types';
import { FadeSlideIn, HeaderSection, ModalBody, PartnershipCard, IconCircle } from './elcStyles';

function formatCountyList(counties: string[]): string {
    if (counties.length === 0) return '';
    if (counties.length === 1) return counties[0];
    if (counties.length === 2) return `${counties[0]} or ${counties[1]}`;
    return `${counties.slice(0, -1).join(', ')}, or ${counties[counties.length - 1]}`;
}

export function ELCEligibilityModal({
    partner,
    open,
    onClose,
}: {
    partner: SubPoolSummary;
    open: boolean;
    onClose: () => void;
}) {
    const history = useHistory();
    const { user, refreshAlreadyLoggedInUser } = useUserContext();
    const countyList = formatCountyList(partner.county_names);
    const attributeId = user?.sub_pool_welcome_attribute?.id;

    function closeModal() {
        if (attributeId) {
            UserAttributesApi.remove(attributeId).then(refreshAlreadyLoggedInUser);
        }
        onClose();
    }

    function handleGoToManageCenters() {
        closeModal();
        history.push('/manage-centers');
    }

    return (
        <SizeableRoundedDialog
            id={`${partner.slug ?? partner.id}-elc-eligibility-modal`}
            closeButton={true}
            open={open}
            onClose={closeModal}
            style={{ maxWidth: 520 }}
            contentStyle={{ padding: 0, overflow: 'hidden' }}
            TransitionProps={{ timeout: 400 }}
        >
            <HeaderSection>
                <FadeSlideIn delay={150}>
                    <IconCircle size={56}>
                        <AutoAwesomeIcon style={{ color: Colors.white, fontSize: 28 }} />
                    </IconCircle>
                </FadeSlideIn>
                <FadeSlideIn delay={250}>
                    <Text variant="display" color={Colors.white} textStyle={{ paddingTop: 12 }}>
                        ELC partnership required for fee-free access
                    </Text>
                </FadeSlideIn>
            </HeaderSection>
            <ModalBody>
                <FadeSlideIn delay={300}>
                    <PartnershipCard>
                        <Text variant="h1" bold>
                            We identified that your center is in {countyList} county, but isn&apos;t contracted with the
                            ELC
                        </Text>
                        <Text variant="body1" color={Colors.mediumGrey} textStyle={{ paddingTop: 12 }}>
                            You can still book substitutes through Tandem, but ELC-contracted providers get access with
                            no additional fees. If you are an ELC-contracted provider, add or update your licensed
                            number to see if you qualify.
                        </Text>
                    </PartnershipCard>
                </FadeSlideIn>
                <FadeSlideIn delay={450}>
                    <PrimaryButton
                        id={`${partner.slug ?? partner.id}-elc-eligibility-cta`}
                        onClick={handleGoToManageCenters}
                        buttonStyle={{ marginTop: 10, width: '100%', borderRadius: 999, padding: '14px 0' }}
                        textStyle={{ fontSize: 16 }}
                    >
                        Go to Manage Centers
                        <ArrowForwardIcon style={{ fontSize: 18, marginLeft: 6, verticalAlign: 'middle' }} />
                    </PrimaryButton>
                </FadeSlideIn>
            </ModalBody>
        </SizeableRoundedDialog>
    );
}
